import { currencyFloatToInteger } from '@acme/b2b/pricing';
import { errorsFromSafeResult, TreeifyError } from '@acme/b2b/validators';
import { NoticesComponentModule } from '@b2b/common/components/notices';
import { TODO, UIBModal } from '@b2b/types/core';
import angular, { IScope } from 'angular';
import { InventoryItem, inventoryItemSchema } from '../types';

export interface InventoryCreateModalData {
  enableSalesTax: boolean;
  inventory: InventoryItem[];
  currentInventory: InventoryItem | null;
}

class InventoryCreateModalController {
  static $inject = ['$scope', '$uibModalInstance', 'TemplatesService', 'MODAL_DATA'];

  isSubmitted = false;
  errorsTree: TreeifyError<InventoryItem> = { errors: [] };
  errors: Array<{ message: string }> = [];

  mode: 'create' | 'edit' = 'create';
  title = 'Create';
  showPrice = true;
  enableSalesTax = false;
  model: InventoryItem = {
    name: '',
    description: '',
    quantity: 0,
    sku: '',
    type: 'doNotTrack',
    price: '0',
    basePrice: null,
    printTicket: true,
    salesChannels: this.TemplatesService.orderSalesChannels(angular.copy(this.TemplatesService.salesChannels)),
  };
  selected = { inventoryType: '' };
  cachedTypes: Array<{ name: string; label: string; visible: boolean }> = [];
  types = [
    { name: 'doNotTrack', label: 'Standard with ticket', visible: true },
    { name: 'doNotTrackNoTicket', label: 'Standard no ticket', visible: true },
    { name: 'membershipCardReplacement', label: 'Membership Card Replacement Fee', visible: true },
  ];

  constructor(
    private $scope: IScope,
    private $uibModalInstance: UIBModal<InventoryItem>,
    private TemplatesService: TODO,
    private MODAL_DATA: InventoryCreateModalData
  ) {}

  $onInit() {
    this.enableSalesTax = this.MODAL_DATA.enableSalesTax;

    if (this.MODAL_DATA.currentInventory) {
      this.mode = 'edit';
      this.title = 'Edit';
      this.model = this.MODAL_DATA.currentInventory;
      if (this.model.type === 'doNotTrack') {
        this.selected.inventoryType = this.types[this.model.printTicket ? 0 : 1].name;
      } else {
        this.selected.inventoryType = this.model.type;
      }
    } else {
      this.selected.inventoryType = this.model.type;
    }

    this.cachedTypes = angular.copy(this.types);
  }

  updateInventoryType() {
    if (this.selected.inventoryType === 'donation') {
      this.showPrice = false;
      this.model.price = '0';
      this.model.basePrice = null;
    } else if (this.selected.inventoryType === 'membershipCardReplacement') {
      const salesChannels = this.model.salesChannels;
      // MVP-10199 - Salesforce integration only
      for (let j = 0, jj = salesChannels.length; j < jj; j++) {
        salesChannels[j].enabled = false;
      }
      this.model.salesChannels = salesChannels;
    } else {
      this.showPrice = true;
    }
  }

  isMode(mode: 'create' | 'edit') {
    return this.mode === mode;
  }

  hasErrors() {
    return this.errors.length > 0;
  }

  trim(value: string) {
    return value.replace(/\s+/g, '').toLowerCase();
  }

  isValid(propertyName: string) {
    return !this.isSubmitted || this.errorsTree.properties?.[propertyName] == null;
  }

  isUnique() {
    const currentName = this.trim(this.model.name);

    for (let i = 0, ii = this.MODAL_DATA.inventory.length; i < ii; i++) {
      const trimmed = this.trim(this.MODAL_DATA.inventory[i].name);

      if (currentName === trimmed) {
        return false;
      }
    }

    return true;
  }

  isValidName(name: string) {
    return name.replace(/\s+/g, '').length > 0;
  }

  isValidPrice(value: string | number | undefined) {
    const price = value == null ? null : currencyFloatToInteger(value);

    if (price == null) {
      return false;
    }

    return price >= 0;
  }

  done() {
    this.isSubmitted = true;
    this.errors = [];

    const result = inventoryItemSchema.safeParse(this.model);
    this.errorsTree = errorsFromSafeResult(result);

    if (this.mode === 'create' && !this.isUnique()) {
      this.errors.push({ message: `An add-on named "${this.model.name}" already exists` });
      return;
    }

    if (this.errorsTree.properties?.name) {
      this.errors.push({ message: 'Please enter a valid name' });
      return;
    }

    if (this.errorsTree.properties?.price) {
      this.errors.push({ message: 'Please enter a valid price' });
      return;
    }

    if (this.errorsTree.properties?.basePrice) {
      this.errors.push({ message: 'Please enter a valid base price' });
      return;
    }

    if (!result.success) {
      return;
    }

    this.success({
      ...result.data,
      printTicket: this.selected.inventoryType === 'doNotTrack',
      type: this.selected.inventoryType === 'doNotTrackNoPrint' ? 'doNotTrack' : this.selected.inventoryType,
    });
  }

  cancel() {
    this.$uibModalInstance.dismiss('cancel');
  }

  success(model: InventoryItem) {
    this.$uibModalInstance.close(model);
  }
}

export const InventoryCreateModalControllerModule = angular
  .module('acme.view.application.inventory.createModal.controllers', [
    'ui.bootstrap',
    'acme.common.service.templates',
    NoticesComponentModule,
  ])
  .controller('InventoryCreateModalController', InventoryCreateModalController).name;
